import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { DataProvider } from './context/DataContext';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { BackToTop } from './components/ui/BackToTop';
import { Home } from './pages/Home';

const Admin = React.lazy(() =>
  import('./pages/Admin').then((module) => ({ default: module.Admin }))
);
import { ErrorPage } from './pages/ErrorPage';
import { LoadingProvider, useLoading } from './context/LoadingContext';
import { Preloader } from './components/ui/Preloader';
import { LoaderSpinner } from './components/ui/LoaderSpinner';
import { ErrorBoundary } from './components/common/ErrorBoundary';

const loadFeatures = () => import('./features/domAnimation').then(res => res.default);
import { LazyMotion } from 'framer-motion';

const AppContent: React.FC = () => {
  const location = useLocation();
  const isAdmin = location.pathname === '/admin';
  const { isLoading } = useLoading();

  return (
    <LazyMotion features={loadFeatures} strict>
      <div className="font-sans antialiased text-white min-h-screen bg-rs-bg selection:bg-rs-bright selection:text-white relative">
        <ErrorBoundary>
          {!isAdmin && <Preloader isLoading={isLoading} />}
          <div
            className="fixed inset-0 pointer-events-none z-[5] opacity-[0.03] mix-blend-overlay"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            }}
          ></div>
          {location.pathname === '/' && <Navbar />}
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <Home />
                  <Footer />
                </>
              }
            />
            <Route
              path="/admin"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen flex items-center justify-center">
                      <LoaderSpinner />
                    </div>
                  }
                >
                  <Admin />
                </Suspense>
              }
            />
            <Route path="*" element={<ErrorPage type="404" />} />
          </Routes>
          <BackToTop />
        </ErrorBoundary>
      </div>
    </LazyMotion>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <DataProvider>
          <LoadingProvider>
            <AppContent />
          </LoadingProvider>
        </DataProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
};

export default App;
